// Copyright (c) Ping. All rights reserved.
//
// NEW Tasks right-panel — a single ~409px column with an internal
// navigation state machine (ROOT folder tree → FOLDER task list →
// TASK detail → NEW/EDIT form, plus an EMPTY state). Built to match
// Figma 1:1 with NativeWind / Tailwind `className` styling (dark-mode
// primary; `dark:` variants drive theming, per the repo CLAUDE.md).
//
// This component is PRESENTATIONAL. The host (`TasksPanelHost`) supplies
// the folders + tasks + member roster and the mutation callbacks, wired
// to the same live `TaskRecord` persister/sync the legacy `<TasksScreen>`
// uses. The panel owns only its own nav + draft-form state.

import React, { useCallback, useMemo, useState } from 'react';
import {
  Image,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from 'react-native';
import {
  BarChart3,
  Calendar as CalendarIcon,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  MoreVertical,
  Plus,
  Search,
  User as UserIcon,
  X,
} from 'lucide-react-native';

import type {
  TaskFolder,
  TaskFolderId,
  TaskId,
  TaskItem,
  TaskPriority,
  TaskState,
} from './legacy/types';
import type { UserId } from '../util/ids';

// ---------------------------------------------------------------------------
// Props contract
// ---------------------------------------------------------------------------

/** A member the panel can assign a task to (drives the Assignee dropdown + row avatars). */
export interface TaskMember {
  readonly id: UserId;
  readonly name: string;
  /** Optional avatar URL; falls back to initials when absent. */
  readonly avatarUri?: string | undefined;
  /** When true the pill renders "You" instead of the member name (Figma). */
  readonly isSelf?: boolean | undefined;
}

/** Fields the form collects to create or update a task. */
export interface TaskDraft {
  readonly title: string;
  readonly description: string;
  readonly state: TaskState;
  readonly priority: TaskPriority;
  readonly assigneeId: UserId | null;
  readonly dueAt: number | null;
}

export interface TasksPanelProps {
  /** Top-level folders ("lists"). Empty → EMPTY state. */
  readonly folders: readonly TaskFolder[];
  /** Every task across folders; the panel scopes by `folderId` itself. */
  readonly tasks: readonly TaskItem[];
  /** Optional per-task long description (keyed by task id) — the wire model
   * doesn't persist this yet, so it's optional and host-supplied. */
  readonly descriptionsById?: Readonly<Record<string, string>> | undefined;
  /** Roster for assignee avatars + the Assignee dropdown. */
  readonly members: readonly TaskMember[];

  /** Toggle a task between completed / pending (checkbox tap). */
  readonly onToggleComplete: (task: TaskItem) => void;
  /** Create or update a task. `id === null` → create in `folderId`. */
  readonly onUpsertTask: (input: {
    readonly id: TaskId | null;
    readonly folderId: TaskFolderId | null;
    readonly draft: TaskDraft;
  }) => void;
  /** Create a new folder ("list") and return its id (or void). */
  readonly onCreateFolder?: ((title: string) => void) | undefined;

  /** Optional: stable "now" for deterministic date rendering in tests. */
  readonly nowMs?: number | undefined;
}

// ---------------------------------------------------------------------------
// Internal nav state machine
// ---------------------------------------------------------------------------

type Nav =
  | { readonly kind: 'root' }
  | { readonly kind: 'folder'; readonly folderId: TaskFolderId }
  | { readonly kind: 'task'; readonly folderId: TaskFolderId; readonly taskId: TaskId }
  | {
      readonly kind: 'form';
      readonly folderId: TaskFolderId;
      readonly taskId: TaskId | null; // null → new
    };

// ---------------------------------------------------------------------------
// Shared style constants (exact Figma values as arbitrary classes)
// ---------------------------------------------------------------------------

// The host docks this in a resizable frame, so fill it rather than
// pinning a fixed width.
const PANEL_W = 'flex-1 min-h-0';
const PANEL_BG = 'bg-light-surface-primary dark:bg-dark-surface-app';
const HEADER_BORDER = 'border-b border-black/5 dark:border-white/[0.06]';
const PILL_BG = 'bg-light-surface-secondary dark:bg-dark-surface-elevated';

/** Figma checkbox ring color per priority. */
const PRIORITY_RING: Record<TaskPriority, string> = {
  urgent: '#E5484D',
  high: '#F5A623',
  medium: '#F5A623',
  low: '#6B7280',
};

const STATE_LABEL: Record<TaskState, string> = {
  backlog: 'Backlog',
  pending: 'To do',
  inProgress: 'In progress',
  completed: 'Done',
  cancelled: 'Cancelled',
};

const PRIORITY_LABEL: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

const PRIORITY_BARS: Record<TaskPriority, string> = {
  low: '#6B7280',
  medium: '#F5A623',
  high: '#F5A623',
  urgent: '#E5484D',
};

const STATE_ORDER: readonly TaskState[] = [
  'backlog',
  'pending',
  'inProgress',
  'completed',
  'cancelled',
];
const PRIORITY_ORDER: readonly TaskPriority[] = ['low', 'medium', 'high', 'urgent'];

// ---------------------------------------------------------------------------
// Date helpers (simple, no external calendar)
// ---------------------------------------------------------------------------

const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatLong(ms: number): string {
  const d = new Date(ms);
  return `${d.getDate()} ${MONTHS_LONG[d.getMonth()]}, ${d.getFullYear()}`;
}

/** Short "Mon 4 May" style used in the filled Date pill. */
function formatShort(ms: number): string {
  const d = new Date(ms);
  return `${WEEKDAYS_SHORT[d.getDay()]} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

function MemberAvatar({
  member,
  size = 24,
}: {
  readonly member: TaskMember | undefined;
  readonly size?: number;
}): React.JSX.Element {
  const initials = (member?.name ?? '?')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <View
      className="items-center justify-center rounded-full overflow-hidden bg-light-surface-elevated dark:bg-dark-surface-elevated"
      style={{ width: size, height: size }}
    >
      {member?.avatarUri ? (
        <Image
          source={{ uri: member.avatarUri }}
          style={{ width: size, height: size }}
          accessibilityIgnoresInvertColors
        />
      ) : (
        <Text
          className="font-semibold text-light-text-secondary dark:text-[#B5BAC3]"
          style={{ fontSize: Math.round(size * 0.42) }}
        >
          {initials}
        </Text>
      )}
    </View>
  );
}

/** Header "New X +" / primary action button (dark pill in Figma). */
function HeaderActionButton({
  label,
  onPress,
  accent = false,
}: {
  readonly label: string;
  readonly onPress: () => void;
  readonly accent?: boolean;
}): React.JSX.Element {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      className={[
        'flex-row items-center gap-1.5 rounded-[10px] px-3 py-1.5',
        accent
          ? 'bg-light-accent-brand dark:bg-dark-accent-brand'
          : 'bg-light-surface-secondary dark:bg-dark-surface-elevated active:opacity-80',
      ].join(' ')}
    >
      <Text
        className={[
          'text-[12px] leading-[17px] font-semibold',
          accent ? 'text-white' : 'text-light-text-primary dark:text-[#F5F6F8]',
        ].join(' ')}
      >
        {label}
      </Text>
      <Plus size={15} color={accent ? '#FFFFFF' : '#B5BAC3'} strokeWidth={2.4} />
    </Pressable>
  );
}

/** A breadcrumb segment row used in FOLDER / TASK / FORM headers. */
function Breadcrumb({
  segments,
}: {
  readonly segments: readonly {
    readonly label: string;
    readonly onPress?: (() => void) | undefined;
    readonly active?: boolean;
  }[];
}): React.JSX.Element {
  return (
    <View className="flex-row items-center flex-1 mr-2" accessibilityRole="header">
      {segments.map((seg, i) => (
        <View key={`${seg.label}-${i}`} className="flex-row items-center shrink">
          {i > 0 ? (
            <ChevronRight size={16} color="#6B7280" style={{ marginHorizontal: 6 }} />
          ) : null}
          <Pressable
            onPress={seg.onPress}
            disabled={seg.onPress === undefined}
            accessibilityRole={seg.onPress ? 'button' : 'text'}
          >
            <Text
              numberOfLines={1}
              className={[
                'text-[14px] leading-[20px]',
                seg.active
                  ? 'font-semibold text-light-text-primary dark:text-[#F5F6F8]'
                  : 'font-normal text-light-text-muted dark:text-[#B5BAC3]',
              ].join(' ')}
            >
              {seg.label}
            </Text>
          </Pressable>
        </View>
      ))}
    </View>
  );
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

export function TasksPanel(props: TasksPanelProps): React.JSX.Element {
  const { folders, tasks, members, descriptionsById, onToggleComplete, onUpsertTask, onCreateFolder, nowMs } = props;

  const [nav, setNav] = useState<Nav>({ kind: 'root' });
  const [query, setQuery] = useState('');

  const memberById = useMemo(() => {
    const m = new Map<string, TaskMember>();
    for (const x of members) m.set(x.id, x);
    return m;
  }, [members]);

  const tasksByFolder = useCallback(
    (folderId: TaskFolderId): readonly TaskItem[] =>
      tasks.filter((t) => t.folderId === folderId),
    [tasks],
  );

  const folderById = useCallback(
    (id: TaskFolderId): TaskFolder | undefined => folders.find((f) => f.id === id),
    [folders],
  );

  const goRoot = useCallback(() => setNav({ kind: 'root' }), []);

  // ----- EMPTY -----
  if (folders.length === 0 && nav.kind === 'root') {
    return (
      <View className={`${PANEL_W} h-full ${PANEL_BG}`}>
        <RootHeader
          label="New list"
          query={query}
          onQuery={setQuery}
          onAction={() => onCreateFolder?.('New list')}
        />
        <EmptyState onNewList={() => onCreateFolder?.('New list')} />
      </View>
    );
  }

  // ----- ROOT (folder tree) -----
  if (nav.kind === 'root') {
    const q = query.trim().toLowerCase();
    const visible = q
      ? folders.filter((f) => f.title.toLowerCase().includes(q))
      : folders;
    return (
      <View className={`${PANEL_W} h-full ${PANEL_BG}`}>
        <RootHeader
          label="New folder"
          query={query}
          onQuery={setQuery}
          onAction={() => onCreateFolder?.('New folder')}
        />
        <ScrollView className="flex-1" contentContainerStyle={{ padding: 12, gap: 8 }}>
          {visible.map((folder) => {
            const childTasks = tasksByFolder(folder.id);
            const subtitle =
              childTasks.length > 0 ? childTasks[0]?.title : undefined;
            return (
              <Pressable
                key={folder.id}
                onPress={() => setNav({ kind: 'folder', folderId: folder.id })}
                accessibilityRole="button"
                accessibilityLabel={folder.title}
                className="flex-row items-center gap-3 rounded-[12px] px-4 py-3 bg-light-surface-secondary dark:bg-dark-surface-panel active:opacity-80"
              >
                <ChevronUp size={18} color="#6B7280" strokeWidth={2.2} />
                <View className="flex-1">
                  <Text
                    numberOfLines={1}
                    className="text-[16px] leading-[22px] font-normal text-light-text-primary dark:text-[#F5F6F8]"
                  >
                    {folder.title}
                  </Text>
                  {subtitle ? (
                    <Text
                      numberOfLines={1}
                      className="mt-0.5 text-[12px] leading-[17px] text-light-text-muted dark:text-dark-text-muted"
                    >
                      {subtitle}
                    </Text>
                  ) : null}
                </View>
              </Pressable>
            );
          })}
        </ScrollView>
      </View>
    );
  }

  // ----- FOLDER (task list) -----
  if (nav.kind === 'folder') {
    const folder = folderById(nav.folderId);
    const folderTasks = tasksByFolder(nav.folderId);
    return (
      <View className={`${PANEL_W} h-full ${PANEL_BG}`}>
        <View className={`flex-row items-center px-4 h-[56px] ${HEADER_BORDER}`}>
          <Breadcrumb
            segments={[
              { label: 'Tasks', onPress: goRoot },
              { label: folder?.title ?? 'Folder', active: true },
            ]}
          />
          <HeaderActionButton
            label="New task"
            onPress={() =>
              setNav({ kind: 'form', folderId: nav.folderId, taskId: null })
            }
          />
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Folder options"
            className="ml-2 p-1 active:opacity-70"
          >
            <MoreVertical size={18} color="#6B7280" />
          </Pressable>
        </View>
        <ScrollView className="flex-1" contentContainerStyle={{ paddingVertical: 8 }}>
          {folderTasks.map((task) => (
            <PanelTaskRow
              key={task.id}
              task={task}
              member={task.assigneeId ? memberById.get(task.assigneeId) : undefined}
              description={descriptionsById?.[task.id]}
              onToggle={() => onToggleComplete(task)}
              onPress={() =>
                setNav({ kind: 'task', folderId: nav.folderId, taskId: task.id })
              }
            />
          ))}
        </ScrollView>
      </View>
    );
  }

  // ----- TASK (detail / read) -----
  if (nav.kind === 'task') {
    const folder = folderById(nav.folderId);
    const task = tasks.find((t) => t.id === nav.taskId);
    if (!task) {
      // The task vanished (e.g. deleted on another device) — fall back.
      return (
        <View className={`${PANEL_W} h-full ${PANEL_BG}`}>
          <View className={`flex-row items-center px-4 h-[56px] ${HEADER_BORDER}`}>
            <Breadcrumb
              segments={[
                { label: 'Tasks', onPress: goRoot },
                {
                  label: folder?.title ?? 'Folder',
                  onPress: () => setNav({ kind: 'folder', folderId: nav.folderId }),
                  active: true,
                },
              ]}
            />
          </View>
        </View>
      );
    }
    return (
      <TaskDetail
        task={task}
        folderTitle={folder?.title ?? 'Folder'}
        description={descriptionsById?.[task.id]}
        member={task.assigneeId ? memberById.get(task.assigneeId) : undefined}
        nowMs={nowMs}
        onBackTasks={goRoot}
        onBackFolder={() => setNav({ kind: 'folder', folderId: nav.folderId })}
        onEdit={() =>
          setNav({ kind: 'form', folderId: nav.folderId, taskId: task.id })
        }
      />
    );
  }

  // ----- FORM (new / edit) -----
  const formFolder = folderById(nav.folderId);
  const editing = nav.taskId ? tasks.find((t) => t.id === nav.taskId) ?? null : null;
  return (
    <TaskForm
      key={nav.taskId ?? 'new'}
      folderTitle={formFolder?.title ?? 'Folder'}
      editing={editing}
      description={editing ? descriptionsById?.[editing.id] : undefined}
      members={members}
      nowMs={nowMs}
      onBackTasks={goRoot}
      onBackFolder={() => setNav({ kind: 'folder', folderId: nav.folderId })}
      onCancel={() =>
        editing
          ? setNav({ kind: 'task', folderId: nav.folderId, taskId: editing.id })
          : setNav({ kind: 'folder', folderId: nav.folderId })
      }
      onSubmit={(draft) => {
        onUpsertTask({
          id: editing?.id ?? null,
          folderId: nav.folderId,
          draft,
        });
        if (editing) {
          setNav({ kind: 'task', folderId: nav.folderId, taskId: editing.id });
        } else {
          setNav({ kind: 'folder', folderId: nav.folderId });
        }
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// ROOT header (title + search + action)
// ---------------------------------------------------------------------------

function RootHeader({
  label,
  query,
  onQuery,
  onAction,
}: {
  readonly label: string;
  readonly query: string;
  readonly onQuery: (q: string) => void;
  readonly onAction: () => void;
}): React.JSX.Element {
  const [searching, setSearching] = useState(false);
  return (
    <View className={`flex-row items-center px-4 h-[56px] ${HEADER_BORDER}`}>
      {searching ? (
        <TextInput
          autoFocus
          value={query}
          onChangeText={onQuery}
          onBlur={() => {
            if (query.trim() === '') setSearching(false);
          }}
          placeholder="Search tasks"
          placeholderTextColor="#6B7280"
          className="flex-1 text-[16px] text-light-text-primary dark:text-[#F5F6F8]"
        />
      ) : (
        <Text className="flex-1 font-sans text-[16px] leading-[22px] font-bold text-light-text-primary dark:text-[#F5F6F8]">
          Tasks
        </Text>
      )}
      <Pressable
        onPress={() => setSearching((s) => !s)}
        accessibilityRole="button"
        accessibilityLabel="Search tasks"
        className="p-1.5 mr-1 active:opacity-70"
      >
        <Search size={20} color="#6B7280" />
      </Pressable>
      <HeaderActionButton label={label} onPress={onAction} />
    </View>
  );
}

// ---------------------------------------------------------------------------
// EMPTY state
// ---------------------------------------------------------------------------

function EmptyState({ onNewList }: { readonly onNewList: () => void }): React.JSX.Element {
  return (
    <View className="flex-1 items-center justify-center px-12">
      <View className="items-center justify-center w-[88px] h-[88px] rounded-[22px] bg-light-surface-elevated dark:bg-dark-surface-elevated">
        <CheckCircle2 size={44} color="#1B1D21" strokeWidth={2.5} />
      </View>
      <Text className="mt-7 text-center text-[16px] leading-[22px] text-light-text-muted dark:text-[#B5BAC3]">
        Stay organized together — add tasks, set due dates, and check them off as you go.
      </Text>
      <View className="mt-6">
        <HeaderActionButton label="New list" onPress={onNewList} accent />
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Task row (FOLDER view)
// ---------------------------------------------------------------------------

function PanelTaskRow({
  task,
  member,
  description,
  onToggle,
  onPress,
}: {
  readonly task: TaskItem;
  readonly member: TaskMember | undefined;
  readonly description: string | undefined;
  readonly onToggle: () => void;
  readonly onPress: () => void;
}): React.JSX.Element {
  const completed = task.state === 'completed';
  const ring = PRIORITY_RING[task.priority];
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={task.title}
      className="flex-row items-start gap-3 px-4 py-3 active:bg-black/[0.03] dark:active:bg-white/[0.03]"
    >
      <Pressable
        onPress={onToggle}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: completed }}
        accessibilityLabel={`Toggle ${task.title}`}
        hitSlop={8}
        className="mt-0.5 items-center justify-center rounded-full"
        style={{
          width: 22,
          height: 22,
          borderWidth: 2,
          borderColor: ring,
          backgroundColor: completed ? ring : 'transparent',
        }}
      >
        {completed ? <Check size={13} color="#FFFFFF" strokeWidth={3} /> : null}
      </Pressable>
      <View className="flex-1">
        <Text
          numberOfLines={1}
          className={[
            'text-[16px] leading-[22px] font-normal',
            completed
              ? 'text-light-text-muted line-through dark:text-dark-text-muted'
              : 'text-light-text-primary dark:text-[#F5F6F8]',
          ].join(' ')}
        >
          {task.title}
        </Text>
        {description ? (
          <Text
            numberOfLines={1}
            className="mt-0.5 text-[12px] leading-[17px] text-light-text-muted dark:text-dark-text-muted"
          >
            {description}
          </Text>
        ) : null}
      </View>
      <View className="ml-2 mt-0.5">
        <MemberAvatar member={member} size={24} />
      </View>
    </Pressable>
  );
}

// ---------------------------------------------------------------------------
// Filled pill (read mode) + dropdown pill (edit mode)
// ---------------------------------------------------------------------------

function PriorityBars({ priority }: { readonly priority: TaskPriority }): React.JSX.Element {
  const color = PRIORITY_BARS[priority];
  const heights = [6, 9, 12];
  const lit =
    priority === 'low' ? 1 : priority === 'medium' ? 2 : 3; // high/urgent → 3
  return (
    <View className="flex-row items-end gap-[2px]" style={{ height: 12 }}>
      {heights.map((h, i) => (
        <View
          key={h}
          style={{
            width: 3,
            height: h,
            borderRadius: 1,
            backgroundColor: i < lit ? color : '#4B5563',
          }}
        />
      ))}
    </View>
  );
}

function FilledPill({
  icon,
  label,
  onPress,
  onRemove,
}: {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly onPress?: (() => void) | undefined;
  readonly onRemove?: (() => void) | undefined;
}): React.JSX.Element {
  return (
    <Pressable
      onPress={onPress}
      disabled={onPress === undefined && onRemove === undefined}
      accessibilityRole="button"
      accessibilityLabel={label}
      className={`flex-row items-center gap-2 rounded-[10px] px-3 py-2 ${PILL_BG} active:opacity-80`}
    >
      {icon}
      <Text className="text-[14px] font-medium text-light-text-primary dark:text-[#F5F6F8]">
        {label}
      </Text>
      {onRemove ? (
        <Pressable onPress={onRemove} hitSlop={8} accessibilityLabel={`Clear ${label}`}>
          <X size={15} color="#6B7280" />
        </Pressable>
      ) : (
        <ChevronDown size={15} color="#6B7280" />
      )}
    </Pressable>
  );
}

// ---------------------------------------------------------------------------
// TASK detail (read)
// ---------------------------------------------------------------------------

function TaskDetail({
  task,
  folderTitle,
  description,
  member,
  nowMs,
  onBackTasks,
  onBackFolder,
  onEdit,
}: {
  readonly task: TaskItem;
  readonly folderTitle: string;
  readonly description: string | undefined;
  readonly member: TaskMember | undefined;
  readonly nowMs: number | undefined;
  readonly onBackTasks: () => void;
  readonly onBackFolder: () => void;
  readonly onEdit: () => void;
}): React.JSX.Element {
  const created = task.createdAt || nowMs || Date.now();
  return (
    <View className={`${PANEL_W} h-full ${PANEL_BG}`}>
      <View className={`flex-row items-center px-4 h-[56px] ${HEADER_BORDER}`}>
        <Breadcrumb
          segments={[
            { label: 'Tasks', onPress: onBackTasks },
            { label: folderTitle, onPress: onBackFolder },
            { label: task.title, active: true },
          ]}
        />
      </View>

      <ScrollView className="flex-1" contentContainerStyle={{ padding: 20 }}>
        <Text className="text-[28px] leading-[34px] font-bold text-light-text-primary dark:text-[#F5F6F8]">
          {task.title}
        </Text>
        <Text className="mt-1 text-[14px] leading-[20px] text-light-text-muted dark:text-dark-text-muted">
          {formatLong(created)}
        </Text>
        {description ? (
          <Text className="mt-4 text-[16px] leading-[24px] text-light-text-secondary dark:text-[#B5BAC3]">
            {description}
          </Text>
        ) : null}

        <View className="mt-5 border-t border-black/5 dark:border-white/[0.06]" />

        <View className="mt-5 flex-row flex-wrap gap-2">
          <FilledPill
            icon={<Circle size={16} color="#6B7280" strokeWidth={2} />}
            label={STATE_LABEL[task.state]}
          />
          <FilledPill
            icon={<PriorityBars priority={task.priority} />}
            label={PRIORITY_LABEL[task.priority]}
          />
          <FilledPill
            icon={<MemberAvatar member={member} size={18} />}
            label={member?.isSelf ? 'You' : member?.name ?? 'Unassigned'}
          />
          <FilledPill
            icon={<CalendarIcon size={16} color="#6B7280" strokeWidth={2} />}
            label={task.dueAt ? formatShort(task.dueAt) : 'No date'}
          />
        </View>
      </ScrollView>

      <View className="flex-row justify-end px-5 py-4">
        <Pressable
          onPress={onEdit}
          accessibilityRole="button"
          accessibilityLabel="Edit Task"
          className="rounded-[10px] bg-light-accent-brand dark:bg-dark-accent-brand px-5 py-2.5"
        >
          <Text className="text-[14px] leading-[20px] font-semibold text-white">Edit Task</Text>
        </Pressable>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// FORM (new / edit)
// ---------------------------------------------------------------------------

type OpenMenu = 'status' | 'priority' | 'assignee' | 'date' | null;

function TaskForm({
  folderTitle,
  editing,
  description,
  members,
  nowMs,
  onBackTasks,
  onBackFolder,
  onCancel,
  onSubmit,
}: {
  readonly folderTitle: string;
  readonly editing: TaskItem | null;
  readonly description: string | undefined;
  readonly members: readonly TaskMember[];
  readonly nowMs: number | undefined;
  readonly onBackTasks: () => void;
  readonly onBackFolder: () => void;
  readonly onCancel: () => void;
  readonly onSubmit: (draft: TaskDraft) => void;
}): React.JSX.Element {
  const [title, setTitle] = useState(editing?.title ?? '');
  const [desc, setDesc] = useState(description ?? '');
  const [state, setState] = useState<TaskState>(editing?.state ?? 'pending');
  const [priority, setPriority] = useState<TaskPriority>(editing?.priority ?? 'medium');
  const [assigneeId, setAssigneeId] = useState<UserId | null>(editing?.assigneeId ?? null);
  const [dueAt, setDueAt] = useState<number | null>(editing?.dueAt ?? null);
  const [menu, setMenu] = useState<OpenMenu>(null);

  const isEdit = editing !== null;
  const titleSet = title.trim().length > 0;
  const now = nowMs ?? Date.now();
  const assignee = assigneeId ? members.find((m) => m.id === assigneeId) ?? null : null;

  const submit = useCallback(() => {
    if (!titleSet) return;
    onSubmit({
      title: title.trim(),
      description: desc.trim(),
      state,
      priority,
      assigneeId,
      dueAt,
    });
  }, [titleSet, onSubmit, title, desc, state, priority, assigneeId, dueAt]);

  // The 4 dropdown pills. In edit mode they render filled (with × to clear);
  // in new mode they render as labelled dropdown triggers (Figma).
  const statusLabel = isEdit ? STATE_LABEL[state] : 'Status';
  const priorityLabel = isEdit ? PRIORITY_LABEL[priority] : 'Priority';
  const assigneeLabel = isEdit
    ? assignee?.isSelf
      ? 'You'
      : assignee?.name ?? 'Assignee'
    : 'Assignee';
  const dateLabel = isEdit && dueAt ? formatShort(dueAt) : 'Date';

  return (
    <View className={`${PANEL_W} h-full ${PANEL_BG}`}>
      <View className={`flex-row items-center px-4 h-[56px] ${HEADER_BORDER}`}>
        <Breadcrumb
          segments={[
            { label: 'Tasks', onPress: onBackTasks },
            { label: folderTitle, onPress: onBackFolder },
            { label: isEdit ? editing.title : 'New Task', active: true },
          ]}
        />
      </View>

      <ScrollView
        className="flex-1"
        contentContainerStyle={{ padding: 20 }}
        keyboardShouldPersistTaps="handled"
      >
        <TextInput
          value={title}
          onChangeText={setTitle}
          placeholder="Task title"
          placeholderTextColor="#5A5F68"
          className="text-[28px] leading-[34px] font-bold text-light-text-primary dark:text-[#F5F6F8]"
        />
        <Text className="mt-1 text-[14px] leading-[20px] text-light-text-muted dark:text-dark-text-muted">
          {formatLong(editing?.createdAt ?? now)}
        </Text>
        <TextInput
          value={desc}
          onChangeText={setDesc}
          placeholder="Description (optional)"
          placeholderTextColor="#5A5F68"
          multiline
          className="mt-4 text-[16px] leading-[24px] text-light-text-secondary dark:text-[#B5BAC3]"
        />

        <View className="mt-5 border-t border-black/5 dark:border-white/[0.06]" />

        <View className="mt-5 flex-row flex-wrap gap-2">
          <FilledPill
            icon={<Circle size={16} color="#6B7280" strokeWidth={2} />}
            label={statusLabel}
            onPress={() => setMenu((m) => (m === 'status' ? null : 'status'))}
            onRemove={isEdit ? () => setState('pending') : undefined}
          />
          <FilledPill
            icon={<BarChart3 size={16} color="#6B7280" strokeWidth={2} />}
            label={priorityLabel}
            onPress={() => setMenu((m) => (m === 'priority' ? null : 'priority'))}
            onRemove={isEdit ? () => setPriority('medium') : undefined}
          />
          <FilledPill
            icon={
              isEdit && assignee ? (
                <MemberAvatar member={assignee} size={18} />
              ) : (
                <UserIcon size={16} color="#6B7280" strokeWidth={2} />
              )
            }
            label={assigneeLabel}
            onPress={() => setMenu((m) => (m === 'assignee' ? null : 'assignee'))}
            onRemove={isEdit && assignee ? () => setAssigneeId(null) : undefined}
          />
          <FilledPill
            icon={<CalendarIcon size={16} color="#6B7280" strokeWidth={2} />}
            label={dateLabel}
            onPress={() => setMenu((m) => (m === 'date' ? null : 'date'))}
            onRemove={isEdit && dueAt ? () => setDueAt(null) : undefined}
          />
        </View>

        {menu ? (
          <View className="mt-3 rounded-[12px] bg-light-surface-secondary dark:bg-dark-surface-panel border border-black/5 dark:border-white/[0.06] overflow-hidden">
            {menu === 'status' &&
              STATE_ORDER.map((s) => (
                <DropdownOption
                  key={s}
                  label={STATE_LABEL[s]}
                  selected={s === state}
                  onPress={() => {
                    setState(s);
                    setMenu(null);
                  }}
                />
              ))}
            {menu === 'priority' &&
              PRIORITY_ORDER.map((p) => (
                <DropdownOption
                  key={p}
                  label={PRIORITY_LABEL[p]}
                  selected={p === priority}
                  leading={<PriorityBars priority={p} />}
                  onPress={() => {
                    setPriority(p);
                    setMenu(null);
                  }}
                />
              ))}
            {menu === 'assignee' && (
              <>
                <DropdownOption
                  label="Unassigned"
                  selected={assigneeId === null}
                  onPress={() => {
                    setAssigneeId(null);
                    setMenu(null);
                  }}
                />
                {members.map((m) => (
                  <DropdownOption
                    key={m.id}
                    label={m.isSelf ? `${m.name} (You)` : m.name}
                    selected={assigneeId === m.id}
                    leading={<MemberAvatar member={m} size={20} />}
                    onPress={() => {
                      setAssigneeId(m.id);
                      setMenu(null);
                    }}
                  />
                ))}
              </>
            )}
            {menu === 'date' && (
              <DateChooser
                value={dueAt}
                nowMs={now}
                onPick={(ms) => {
                  setDueAt(ms);
                  setMenu(null);
                }}
              />
            )}
          </View>
        ) : null}
      </ScrollView>

      <View className="flex-row items-center justify-end gap-4 px-5 py-4">
        <Pressable
          onPress={onCancel}
          accessibilityRole="button"
          accessibilityLabel="Cancel"
          className="px-2 py-2 active:opacity-70"
        >
          <Text className="text-[14px] leading-[20px] font-medium text-light-text-secondary dark:text-[#B5BAC3]">
            Cancel
          </Text>
        </Pressable>
        <Pressable
          onPress={submit}
          disabled={!titleSet}
          accessibilityRole="button"
          accessibilityState={{ disabled: !titleSet }}
          accessibilityLabel={isEdit ? 'Save' : 'Add Task'}
          className={[
            'rounded-[10px] px-5 py-2.5',
            titleSet
              ? 'bg-light-accent-brand dark:bg-dark-accent-brand'
              : 'bg-light-accent-brand/40 dark:bg-dark-accent-brand/40',
          ].join(' ')}
        >
          <Text className="text-[14px] leading-[20px] font-semibold text-white">
            {isEdit ? 'Save' : 'Add Task'}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

function DropdownOption({
  label,
  selected,
  leading,
  onPress,
}: {
  readonly label: string;
  readonly selected: boolean;
  readonly leading?: React.ReactNode;
  readonly onPress: () => void;
}): React.JSX.Element {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="menuitem"
      accessibilityState={{ selected }}
      className="flex-row items-center gap-2.5 px-4 py-2.5 active:bg-black/[0.04] dark:active:bg-white/[0.04]"
    >
      {leading ? <View className="w-5 items-center">{leading}</View> : null}
      <Text className="flex-1 text-[14px] leading-[20px] text-light-text-primary dark:text-[#F5F6F8]">{label}</Text>
      {selected ? <Check size={16} color="#537EFF" strokeWidth={2.5} /> : null}
    </Pressable>
  );
}

const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** Same wall-clock day (ignores time-of-day). */
function isSameDay(a: number, b: number): boolean {
  const x = new Date(a);
  const y = new Date(b);
  return (
    x.getFullYear() === y.getFullYear() &&
    x.getMonth() === y.getMonth() &&
    x.getDate() === y.getDate()
  );
}

/**
 * A compact MONTH calendar popover: a header with prev/next month
 * arrows + the month label, a weekday strip, and a 6-row day grid.
 * Tapping a day fires `onPick` with that day's noon-ms (stable across
 * DST). Today and the selected day get distinct treatments.
 */
function DateChooser({
  value,
  nowMs,
  onPick,
}: {
  readonly value: number | null;
  readonly nowMs: number;
  readonly onPick: (ms: number) => void;
}): React.JSX.Element {
  // Which month the grid is showing. Seed to the selected day's month,
  // else the current month. Held as a {year, month} cursor.
  const seed = value ?? nowMs;
  const [view, setView] = useState(() => {
    const d = new Date(seed);
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const cells = useMemo(() => {
    // Leading blanks for the day-of-week the 1st falls on, then each day
    // of the month; padded to complete 6 rows (42 cells) so the grid
    // height stays stable across months.
    const first = new Date(view.year, view.month, 1);
    const lead = first.getDay();
    const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
    const out: Array<number | null> = [];
    for (let i = 0; i < lead; i += 1) out.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) {
      const d = new Date(view.year, view.month, day, 12, 0, 0, 0);
      out.push(d.getTime());
    }
    while (out.length < 42) out.push(null);
    return out;
  }, [view]);

  const goPrev = useCallback(
    () =>
      setView((v) =>
        v.month === 0
          ? { year: v.year - 1, month: 11 }
          : { year: v.year, month: v.month - 1 },
      ),
    [],
  );
  const goNext = useCallback(
    () =>
      setView((v) =>
        v.month === 11
          ? { year: v.year + 1, month: 0 }
          : { year: v.year, month: v.month + 1 },
      ),
    [],
  );

  return (
    <View className="px-3 py-3">
      {/* Month header with prev/next */}
      <View className="flex-row items-center justify-between px-1 pb-2">
        <Pressable
          onPress={goPrev}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Previous month"
          className="p-1 active:opacity-70"
        >
          <ChevronLeft size={18} color="#6B7280" strokeWidth={2.2} />
        </Pressable>
        <Text className="text-[14px] leading-[20px] font-semibold text-light-text-primary dark:text-[#F5F6F8]">
          {MONTHS_LONG[view.month]} {view.year}
        </Text>
        <Pressable
          onPress={goNext}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Next month"
          className="p-1 active:opacity-70"
        >
          <ChevronRight size={18} color="#6B7280" strokeWidth={2.2} />
        </Pressable>
      </View>

      {/* Weekday strip */}
      <View className="flex-row">
        {WEEKDAY_INITIALS.map((w, i) => (
          <View key={`${w}-${i}`} className="flex-1 items-center py-1">
            <Text className="text-[11px] font-medium text-[#6B7280]">{w}</Text>
          </View>
        ))}
      </View>

      {/* Day grid (6 rows × 7) */}
      <View className="flex-row flex-wrap">
        {cells.map((ms, i) => {
          if (ms === null) {
            return <View key={`blank-${i}`} className="w-[14.2857%] aspect-square" />;
          }
          const selected = value !== null && isSameDay(value, ms);
          const today = isSameDay(nowMs, ms);
          return (
            <View key={ms} className="w-[14.2857%] aspect-square items-center justify-center p-0.5">
              <Pressable
                onPress={() => onPick(ms)}
                accessibilityRole="button"
                accessibilityState={{ selected }}
                accessibilityLabel={formatLong(ms)}
                className={[
                  'w-8 h-8 items-center justify-center rounded-full',
                  selected
                    ? 'bg-light-accent-brand dark:bg-dark-accent-brand'
                    : today
                    ? 'border border-light-accent-brand dark:border-dark-accent-brand'
                    : 'active:bg-black/[0.04] dark:active:bg-white/[0.06]',
                ].join(' ')}
              >
                <Text
                  className={[
                    'text-[14px]',
                    selected
                      ? 'font-semibold text-white'
                      : today
                      ? 'font-semibold text-light-accent-brand dark:text-dark-accent-brand'
                      : 'text-light-text-primary dark:text-[#F5F6F8]',
                  ].join(' ')}
                >
                  {new Date(ms).getDate()}
                </Text>
              </Pressable>
            </View>
          );
        })}
      </View>
    </View>
  );
}
